import React, { useState, useEffect, useContext } from 'react';
import { addOutline, closeOutline } from 'ionicons/icons';
import { useHistory, useLocation } from 'react-router-dom';
import {
  IonPage,
  IonContent,
  IonButton,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonTitle,
  IonIcon,
  IonSegment,
  IonLabel,
  IonSegmentButton,
  IonBadge,
} from '@ionic/react';
import { AppContext, AppDispatchContext, ACTIONS } from '../../../global/AppContext';
import { allUsers, getFriendsProfile } from '../../../global/request/user';
import useProfile from '../../../global/useProfile';
import { createConversationChannel, login } from '../../../services/amityClient';
import { AuthContext } from '../../authentication/AuthContext';
import RecentChats from '../../chat/RecentChats';
import NewChatModal from './NewChatModal';
import Notifications from './Notifications';

import '../../../styles/home/notificationModal.scss';

const HomepageNotification = () => {
  const location = useLocation();
  const [selectedSegment, setSelectedSegment] = useState('notifications');
  const authCtx = useContext(AuthContext);
  const appCtx = useContext(AppContext);
  const { dispatch } = useContext(AppDispatchContext);
  const bearerToken = authCtx.tokens.idToken;
  const history = useHistory();
  // eslint-disable-next-line no-unused-vars
  const { unreadCount, notificationUnread, notificationAlert } = appCtx;
  const [users, setUsers] = useState([]);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [reload, profileData] = useProfile();

  useEffect(() => {
    if (location.state?.openChat && selectedSegment !== 'chat') {
      setSelectedSegment('chat');
    }
    // selected segment in dependency array doesn't allow to switch segments
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => {
    const ensureLogin = async () => {
      if (authCtx.accessData && authCtx.accessData.sub && bearerToken) {
        await login(authCtx.accessData.sub, authCtx.idData.name, bearerToken);
      }
    };

    ensureLogin();
  }, [authCtx, bearerToken]);

  const handleChatClick = (channelId, displayName, creatorName) => {
    const chatName = displayName !== profileData.username ? displayName : creatorName;
    history.push(`/chat/${channelId}`, { friendName: chatName });
  };

  const resetNotUnread = () => {
    localStorage.setItem('newNotificationAlert', 'false');
    dispatch({ action: ACTIONS.SET_NOTIFICATION_ALERT, notificationAlert: false });
    localStorage.setItem('notificationUnread', 0);
    dispatch({
      action: ACTIONS.SET_NOTIFICATION_UNREAD,
      notificationUnread: 0,
    });
  };

  const handleNotClick = item => {
    if (item.notification_type === 'challenge') {
      history.push('/app/profile/');
      resetNotUnread();
    } else {
      console.warn('Unhandled notification type:', item.notification_type);
    }
  };

  // SEARCH USERS
  useEffect(() => {
    const fetchUsers = async () => {
      const fetchedUsers = await allUsers(bearerToken);
      setUsers(fetchedUsers);
    };

    fetchUsers();
  }, [bearerToken]);

  const openNewChatModal = () => {
    setIsChatModalOpen(true);
  };

  const closeNewChatModal = () => {
    setIsChatModalOpen(false);
  };

  const handleChatButtonClick = async friendProfileData => {
    if (friendProfileData) {
      const userIds = [authCtx.accessData.sub, friendProfileData.id];
      try {
        const channel = await createConversationChannel(userIds, friendProfileData.username, profileData.username);

        if (channel) {
          history.push({
            pathname: `/chat/${channel.channelId}`,
            state: { friendName: friendProfileData.username, creatorName: profileData.username },
          });
        }
      } catch (error) {
        console.error('Error creating or finding conversation channel:', error);
      }
    } else {
      console.error('No friendProfileData available to start a chat.');
    }
  };

  const handleUserClick = async userId => {
    try {
      const friendProfileData = await getFriendsProfile(bearerToken, userId);
      handleChatButtonClick(friendProfileData);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
    }
  };

  const onClose = () => {
    history.push('/app/home', { openChat: false });
    setSelectedSegment('notifications');
    resetNotUnread();
  };

  return (
    <IonPage className='notification-page'>
      <IonHeader>
        <IonToolbar className='toolbar'>
          <IonButton className='spacing-btn' slot='start'>
            <IonIcon icon={closeOutline} slot='icon-only' />
          </IonButton>
          <IonTitle className='ion-no-padding'>
            <div className='ion-text-wrap centered-title'>Notification Center</div>
          </IonTitle>
          <IonButtons slot='end'>
            <IonButton onClick={onClose} slot='end'>
              <IonIcon icon={closeOutline} slot='icon-only' />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className='segments'>
          <IonSegment
            mode='md'
            className='notification-segment'
            value={selectedSegment}
            onIonChange={e => {
              const selectedValue = e.detail.value;
              setSelectedSegment(selectedValue);
              if (selectedValue === 'notifications') {
                resetNotUnread();
              }
            }}>
            <IonSegmentButton value='notifications' className='segment-btn' onClick={resetNotUnread}>
              <div className='label-badge-container'>
                <IonLabel className='chat-label'>Notifications</IonLabel>
                {notificationUnread > 0 && notificationAlert && (
                  <IonBadge color='danger' className='recent-badge'>
                    {Number(notificationUnread)}
                  </IonBadge>
                )}
              </div>
            </IonSegmentButton>
            <IonSegmentButton value='chat' className='segment-btn'>
              <div className='label-badge-container'>
                <IonLabel className='chat-label'>Chat</IonLabel>
                {unreadCount > 0 && (
                  <IonBadge color='danger' className='recent-badge'>
                    {unreadCount}
                  </IonBadge>
                )}
              </div>
            </IonSegmentButton>
          </IonSegment>
        </div>
        <div className='tab-content'>
          {selectedSegment === 'notifications' && <Notifications handleChannelClick={handleNotClick} />}
        </div>
        {selectedSegment === 'chat' && <RecentChats handleChannelClick={handleChatClick} />}
      </IonContent>
      {selectedSegment === 'chat' && (
        <>
          <div className='new-chat'>
            <IonButton className='new-chat-button' onClick={openNewChatModal}>
              <IonIcon icon={addOutline} slot='icon-only' />
            </IonButton>
          </div>
          <NewChatModal
            isOpen={isChatModalOpen}
            onClose={closeNewChatModal}
            users={users}
            handleUserClick={handleUserClick}
          />
        </>
      )}
    </IonPage>
  );
};

export default HomepageNotification;
